/**
 * CalendarBox — personal learner calendar (month + week grid, CRUD).
 *
 * <p>CSS-grid month/week calendar — no FullCalendar/react-big-calendar dep,
 * theme-agnostic (uses shared design tokens via CSS custom properties so it
 * renders correctly under any theme). Self-fetches from the host BFF:
 *   POST /api/calendar/events/search  — load events for visible window
 *   POST /api/calendar/events         — create USER_MANAGED event
 *   PUT  /api/calendar/events/{id}    — update USER_MANAGED event
 *   DELETE /api/calendar/events/{id}  — delete USER_MANAGED event
 *
 * Backend entityType values:
 *   USER_MANAGED — learner-created, full CRUD
 *   HALL_EVENT   — LMS scheduled session, read-only
 *   LEARNING_DETAIL — catalog item, read-only
 *
 * Filter toggles (legacy MyCalendar parity):
 *   workWeek — hide Saturday + Sunday columns/cells
 *   workHour — restrict week-view rows to 06:00–17:59 (business hours)
 *
 * <p>Month view renders multi-day events as spanning bars (one bar per
 * event per week row, clipped to that row and laid out into stacking
 * lanes) instead of a chip on the start day only — matches how every
 * mainstream calendar UI (Google/Outlook/FullCalendar) shows a range.
 *
 * <p>All day/month labels are locale-aware (driven by the site-builder
 * runtime's active locale, not the browser default).
 *
 * [legacy ref: akademi-lms-v2 pages/calendar.js + components/calendar/MyCalendar.js]
 */
'use client';

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactElement,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentUser, useLocale } from '@akademi/site-builder-runtime';
import { SectionShell } from '../_shared/SectionShell';
import { type PaddingBox } from '../_shared/resolvePadding';
import { CalendarToolbar } from './CalendarToolbar';
import {
  Button,
  Card,
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DateTimeField,
} from '@akademi/ui';
import { cardReset } from '../_shared/styles';

/* ─── Types ────────────────────────────────────────────────────────────── */

interface CalEvent {
  id: string | number;
  title: string;
  startTime?: string;
  endTime?: string;
  description?: string;
  entityType?: string; // USER_MANAGED | HALL_EVENT | LEARNING_DETAIL
}

type CalView = 'month' | 'week';

export interface CalendarBoxProps {
  title?: string;
  subtitle?: string;
  /** Design token size key e.g. "xl", "2xl". Maps to var(--font-size-{key}). */
  titleFontSize?: string;
  /** Default view shown on load. */
  defaultView?: CalView;
  /** Label for the "Add event" button. Defaults to the active locale's `button.addEvent`. */
  addEventLabel?: string;
  /** Label shown when no events exist in the visible window. */
  emptyLabel?: string;
  signedOutTitle?: string;
  signedOutBody?: string;
  signInLabel?: string;
  signInHref?: string;
  /** Hide Saturday + Sunday (operator default; user can toggle at runtime). */
  defaultWorkWeek?: boolean;
  /** Restrict week-view rows to 06:00–17:59 (operator default; user can toggle). */
  defaultWorkHour?: boolean;
  /** Label for the Work week toggle. Defaults to the active locale's `label.workWeek`. */
  workWeekLabel?: string;
  /** Label for the Work hours toggle. Defaults to the active locale's `label.workHour`. */
  workHourLabel?: string;
  /** Label for the "Today" nav button. Defaults to the active locale's `label.today`. */
  todayLabel?: string;
  /** Accessible label for the previous-period nav button. Defaults to the active locale's `label.previous`. */
  prevLabel?: string;
  /** Accessible label for the next-period nav button. Defaults to the active locale's `label.next`. */
  nextLabel?: string;
  /** Weekday header format — abbreviated ("Mon", "PZT") or full ("Monday",
   *  "Pazartesi"). Default `short`. */
  dayFormat?: 'short' | 'long';
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

/* ─── Helpers ──────────────────────────────────────────────────────────── */

function apiUrl(path: string): string {
  const base = (process.env.NEXT_PUBLIC_BASE_PATH ?? '').replace(/\/+$/, '');
  return `${base}${path}`;
}

/** Mon-first weekday indices considered weekend (Sat=5, Sun=6). */
const WEEKEND_INDICES = new Set([5, 6]);
const WORK_HOUR_START = 6;
const WORK_HOUR_END = 18; // exclusive — rows 6..17 shown
const MAX_LANES = 3;
const LANE_HEIGHT = 20;
const LANE_GAP = 3;
const DAYNUM_HEIGHT = 30;

/** Monday-first week index 0–6 */
function weekdayIndex(d: Date): number {
  return (d.getDay() + 6) % 7;
}

/** Local-calendar-date key (YYYY-MM-DD), NOT UTC. `toISOString()` converts
 *  through UTC first, which silently shifts every date by one day for any
 *  positive-UTC-offset timezone (e.g. Istanbul, UTC+3) since local midnight
 *  falls on the *previous* UTC day — that misaligned every "is this today?"
 *  / day-bucketing comparison in this file. */
function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function startOfMonth(y: number, m: number): Date {
  return new Date(y, m, 1);
}

/** Build a 6-row × 7-col month grid (dates, may include prev/next month). */
function buildMonthCells(year: number, month: number): Date[] {
  const first = startOfMonth(year, month);
  const pivot = weekdayIndex(first);
  const cells: Date[] = [];
  for (let i = -pivot; i < 42 - pivot; i++) {
    cells.push(new Date(year, month, 1 + i));
  }
  return cells;
}

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

/** Build hour rows (0–23) × 7-day columns for a week starting on `monday`. */
function getMonday(ref: Date): Date {
  const d = new Date(ref);
  d.setHours(0, 0, 0, 0);
  const dow = weekdayIndex(d);
  d.setDate(d.getDate() - dow);
  return d;
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d);
  r.setDate(r.getDate() + n);
  return r;
}

/** Locale-aware month names, e.g. `['Ocak', 'Şubat', ...]` for tr. */
function monthNames(locale: string): string[] {
  try {
    const fmtM = new Intl.DateTimeFormat(locale, { month: 'long' });
    return Array.from({ length: 12 }, (_, m) => fmtM.format(new Date(2024, m, 1)));
  } catch {
    return Array.from({ length: 12 }, (_, m) => new Date(2024, m, 1).toDateString());
  }
}

/** Locale-aware Mon-first weekday labels, e.g. `['Pzt', 'Sal', ...]` (short)
 *  or `['Pazartesi', 'Salı', ...]` (long) — format is operator-configurable
 *  via the `dayFormat` block prop, not hardcoded. */
function dayNames(locale: string, format: 'short' | 'long' = 'short'): string[] {
  try {
    const fmtD = new Intl.DateTimeFormat(locale, { weekday: format });
    // 2024-01-01 is a Monday — a stable Mon-first reference week.
    return Array.from({ length: 7 }, (_, i) => fmtD.format(new Date(2024, 0, 1 + i)));
  } catch {
    return format === 'long'
      ? ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
      : ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  }
}

function fmt(d: Date, locale: string, opts: Intl.DateTimeFormatOptions): string {
  try {
    return d.toLocaleString(locale, opts);
  } catch {
    return d.toString();
  }
}

function fmtDatetime(iso: string | undefined, locale: string): string {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString(locale, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

/** True when an event spans more than one calendar day — those render as an
 *  all-day spanning bar (month grid, week-view all-day row) instead of a
 *  timed slot chip. */
function isMultiDay(e: CalEvent): boolean {
  if (!e.startTime) return false;
  const s = new Date(e.startTime);
  const en = e.endTime ? new Date(e.endTime) : s;
  return isoDate(s) !== isoDate(en);
}

function eventsInHour(events: CalEvent[], day: Date, hour: number): CalEvent[] {
  const key = isoDate(day);
  return events.filter((e) => {
    if (!e.startTime || isMultiDay(e)) return false;
    const s = new Date(e.startTime);
    return isoDate(s) === key && s.getHours() === hour;
  });
}

interface EventBar {
  event: CalEvent;
  startCol: number;
  span: number;
  lane: number;
}

/** Lay out events overlapping a week row into stacking lanes, clipped to
 *  that row's date range so multi-day events render as a continuous bar
 *  across every week they touch (not just their start day). */
function layoutWeekEvents(
  events: CalEvent[],
  week: Date[],
): { bars: EventBar[]; overflow: Map<string, number> } {
  const rowStart = new Date(week[0]!);
  rowStart.setHours(0, 0, 0, 0);
  const rowEndExcl = addDays(new Date(week[week.length - 1]!), 1);
  rowEndExcl.setHours(0, 0, 0, 0);

  const relevant = events
    .filter((e) => {
      if (!e.startTime) return false;
      const s = new Date(e.startTime);
      const en = e.endTime ? new Date(e.endTime) : s;
      return s < rowEndExcl && en >= rowStart;
    })
    .sort((a, b) => {
      const as = new Date(a.startTime!).getTime();
      const bs = new Date(b.startTime!).getTime();
      if (as !== bs) return as - bs;
      const ae = a.endTime ? new Date(a.endTime).getTime() : as;
      const be = b.endTime ? new Date(b.endTime).getTime() : bs;
      return be - ae; // longer events first within the same start
    });

  const laneOccupancy: EventBar[] = [];
  const bars: EventBar[] = [];
  const overflow = new Map<string, number>();

  for (const e of relevant) {
    const s = new Date(e.startTime!);
    const en = e.endTime ? new Date(e.endTime) : s;
    const clipStart = s < rowStart ? rowStart : s;
    const clipEndInclusiveDay = en >= rowEndExcl ? addDays(rowEndExcl, -1) : en;

    let startCol = week.findIndex((d) => isoDate(d) === isoDate(clipStart));
    let endCol = week.findIndex((d) => isoDate(d) === isoDate(clipEndInclusiveDay));
    if (startCol === -1) startCol = 0;
    if (endCol === -1 || endCol < startCol) endCol = startCol;
    const span = endCol - startCol + 1;

    let lane = 0;
    while (
      laneOccupancy.some(
        (b) => b.lane === lane && !(endCol < b.startCol || startCol > b.startCol + b.span - 1),
      )
    ) {
      lane++;
    }

    if (lane >= MAX_LANES) {
      for (let c = startCol; c <= endCol; c++) {
        const dayKey = isoDate(week[c]!);
        overflow.set(dayKey, (overflow.get(dayKey) ?? 0) + 1);
      }
      continue;
    }

    const bar: EventBar = { event: e, startCol, span, lane };
    laneOccupancy.push(bar);
    bars.push(bar);
  }

  return { bars, overflow };
}

/* ─── Shared CSS tokens ────────────────────────────────────────────────── */

const TOKEN = {
  brand: 'var(--color-brand-500, #29a9e0)',
  brand50: 'var(--color-brand-50, #e9f7fc)',
  brand100: 'var(--color-brand-100, #c9ecf8)',
  brand700: 'var(--color-brand-700, #166a98)',
  surface: 'var(--color-surface-card, #ffffff)',
  surfaceMuted: 'var(--color-surface-muted, #fafafa)',
  border: 'var(--color-border-subtle, rgba(0,0,0,0.08))',
  borderStrong: 'var(--color-border-strong, rgba(0,0,0,0.16))',
  text: 'var(--color-text-primary, #18181b)',
  textSec: 'var(--color-text-secondary, #52525b)',
  textTer: 'var(--color-text-tertiary, #94867d)',
  success: 'var(--color-success-600, #16a34a)',
  warning: 'var(--color-warning-600, #d97706)',
  radius: 'var(--radius-md, 10px)',
  radiusLg: 'var(--radius-lg, 16px)',
  shadow: 'var(--shadow-sm, 0 1px 2px rgba(16,24,40,0.06))',
};

const entityColor: Record<string, string> = {
  USER_MANAGED: TOKEN.brand,
  HALL_EVENT: TOKEN.warning,
  LEARNING_DETAIL: TOKEN.success,
};

function eventColor(event: CalEvent): string {
  return entityColor[event.entityType ?? ''] ?? TOKEN.brand;
}

/** Localized entityType label. `t` is `useTranslation('common')`'s `t`. */
function entityTypeLabel(t: (key: string) => string, type: string): string {
  switch (type) {
    case 'USER_MANAGED':
      return t('label.userManaged') || 'User managed';
    case 'HALL_EVENT':
      return t('label.hallEvent') || 'Hall event';
    case 'LEARNING_DETAIL':
      return t('label.learningDetail') || 'Learning detail';
    default:
      return type.replace(/_/g, ' ');
  }
}

/* ─── Responsive style tag ─────────────────────────────────────────────── */

const CalendarBoxStyles = (): null => {
  if (typeof document === 'undefined') return null;
  const id = 'ak-calendar-box-styles';
  if (!document.getElementById(id)) {
    const s = document.createElement('style');
    s.id = id;
    s.textContent = `
      .ak-cal-root { max-width: 100%; overflow-x: hidden; }
      .ak-cal-daycell:hover { background: var(--color-surface-muted, #fafafa); }
      @media (max-width: 639px) {
        .ak-cal-month-header { font-size: 10px !important; padding: 6px 2px !important; }
        .ak-cal-daynum { width: 22px !important; height: 22px !important; font-size: 11px !important; }
        .ak-cal-bar { font-size: 9.5px !important; padding: 1px 3px !important; }
        .ak-cal-week-grid { min-width: 0 !important; }
        .ak-cal-week-hourcol { display: none !important; }
      }
    `;
    document.head.appendChild(s);
  }
  return null;
};

/* ─── Event bar (spanning, month view) ─────────────────────────────────── */

function EventBarChip({
  event,
  onClick,
  compact,
}: {
  event: CalEvent;
  onClick: (e: CalEvent) => void;
  compact?: boolean;
}): ReactElement {
  const color = eventColor(event);
  return (
    <button
      type="button"
      className="ak-focus ak-cal-bar"
      onClick={(ev) => {
        ev.stopPropagation();
        onClick(event);
      }}
      style={{
        display: 'block',
        width: '100%',
        height: LANE_HEIGHT,
        lineHeight: `${LANE_HEIGHT}px`,
        padding: compact ? '0 4px' : '0 8px',
        borderRadius: 'var(--radius-sm, 5px)',
        background: color,
        color: 'var(--color-text-on-brand, #fff)',
        fontSize: 'var(--font-size-xs, 11px)',
        fontWeight: 600,
        textAlign: 'left',
        border: 'none',
        cursor: 'pointer',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
      }}
      title={event.title}
    >
      {event.title}
    </button>
  );
}

function EventChip({
  event,
  onClick,
}: {
  event: CalEvent;
  onClick: (e: CalEvent) => void;
}): ReactElement {
  const color = eventColor(event);
  return (
    <button
      type="button"
      className="ak-focus"
      onClick={(ev) => {
        ev.stopPropagation();
        onClick(event);
      }}
      style={{
        display: 'block',
        width: '100%',
        padding: '2px 6px',
        marginBottom: 2,
        borderRadius: 'var(--radius-sm, 4px)',
        background: color,
        color: 'var(--color-text-on-brand, #fff)',
        fontSize: 'var(--font-size-xs, 11px)',
        fontWeight: 600,
        lineHeight: 1.4,
        textAlign: 'left',
        border: 'none',
        cursor: 'pointer',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        minBlockSize: 24,
      }}
    >
      {event.title}
    </button>
  );
}

/* ─── Month grid ───────────────────────────────────────────────────────── */

function MonthGrid({
  year,
  month,
  events,
  onDayClick,
  onEventClick,
  workWeek,
  labels,
}: {
  year: number;
  month: number;
  events: CalEvent[];
  onDayClick: (d: Date) => void;
  onEventClick: (e: CalEvent) => void;
  workWeek: boolean;
  labels: string[];
}): ReactElement {
  const today = isoDate(new Date());
  const allCells = buildMonthCells(year, month);
  const cells = workWeek ? allCells.filter((_, idx) => !WEEKEND_INDICES.has(idx % 7)) : allCells;
  const colCount = workWeek ? 5 : 7;
  const dayHeaders = labels.filter((_, i) => !workWeek || !WEEKEND_INDICES.has(i));
  const weeks = useMemo(() => chunk(cells, colCount), [cells, colCount]);

  const rowHeight = DAYNUM_HEIGHT + MAX_LANES * (LANE_HEIGHT + LANE_GAP) + 6;
  // Below this, columns get too narrow for day numbers + event bars to stay
  // legible — fall back to horizontal scroll (same pattern as WeekGrid)
  // instead of letting the grid squash headers into wrapped text.
  const minGridWidth = colCount * 78;

  return (
    <Card
      style={{
        ...cardReset,
        borderRadius: TOKEN.radiusLg,
        overflow: 'hidden',
        background: TOKEN.surface,
      }}
    >
      <div className="ak-cal-month-scroll" style={{ overflowX: 'auto' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${colCount}, 1fr)`,
            minWidth: minGridWidth,
            background: TOKEN.brand50,
            borderBottom: `1px solid ${TOKEN.border}`,
          }}
        >
          {dayHeaders.map((d, i) => (
            <div
              key={`${d}-${i}`}
              className="ak-cal-month-header"
              style={{
                padding: '10px 4px',
                textAlign: 'center',
                fontSize: 'var(--font-size-xs, 12px)',
                fontWeight: 700,
                color: TOKEN.textSec,
                letterSpacing: 'var(--font-tracking-wider, 0.05em)',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
              }}
            >
              {d}
            </div>
          ))}
        </div>

        {weeks.map((week, wi) => {
          const { bars, overflow } = layoutWeekEvents(events, week);
          return (
            <div
              key={wi}
              style={{
                position: 'relative',
                display: 'grid',
                gridTemplateColumns: `repeat(${colCount}, 1fr)`,
                minWidth: minGridWidth,
                minHeight: `clamp(${rowHeight}px, ${rowHeight / 6}vw, ${rowHeight + 16}px)`,
                borderTop: wi > 0 ? `1px solid ${TOKEN.border}` : undefined,
              }}
            >
              {week.map((cell, idx) => {
                const key = isoDate(cell);
                const isCurrentMonth = cell.getMonth() === month;
                const isToday = key === today;
                const hidden = overflow.get(key) ?? 0;
                return (
                  <div
                    key={idx}
                    className="ak-cal-daycell"
                    onClick={() => onDayClick(cell)}
                    style={{
                      borderRight:
                        (idx + 1) % colCount !== 0 ? `1px solid ${TOKEN.border}` : undefined,
                      padding: '5px 4px 2px',
                      background: isToday ? TOKEN.brand50 : 'transparent',
                      cursor: 'pointer',
                      transition: 'background 0.12s',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <div
                      className="ak-cal-daynum"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 26,
                        height: 26,
                        borderRadius: '50%',
                        fontSize: 'var(--font-size-sm, 13px)',
                        fontWeight: isToday ? 800 : 500,
                        color: isToday
                          ? 'var(--color-text-on-brand, #fff)'
                          : isCurrentMonth
                            ? TOKEN.text
                            : TOKEN.textTer,
                        background: isToday ? TOKEN.brand : 'transparent',
                      }}
                    >
                      {cell.getDate()}
                    </div>
                    {hidden > 0 && (
                      <div
                        style={{
                          marginTop: 'auto',
                          fontSize: 'var(--font-size-xs, 10px)',
                          color: TOKEN.textSec,
                          fontWeight: 600,
                          textAlign: 'right',
                        }}
                      >
                        +{hidden}
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Spanning event-bar overlay, positioned below the day-number row */}
              <div
                style={{
                  position: 'absolute',
                  left: 0,
                  right: 0,
                  top: DAYNUM_HEIGHT,
                  display: 'grid',
                  gridTemplateColumns: `repeat(${colCount}, 1fr)`,
                  gridAutoRows: LANE_HEIGHT,
                  rowGap: LANE_GAP,
                  padding: '0 3px',
                  pointerEvents: 'none',
                }}
              >
                {bars.map((b, bi) => (
                  <div
                    key={bi}
                    style={{
                      gridColumn: `${b.startCol + 1} / span ${b.span}`,
                      gridRow: b.lane + 1,
                      padding: '0 1px',
                      pointerEvents: 'auto',
                    }}
                  >
                    <EventBarChip event={b.event} onClick={onEventClick} />
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

/* ─── Week grid ────────────────────────────────────────────────────────── */

function WeekGrid({
  monday,
  events,
  onSlotClick,
  onEventClick,
  workWeek,
  workHour,
  labels,
  locale,
}: {
  monday: Date;
  events: CalEvent[];
  onSlotClick: (d: Date) => void;
  onEventClick: (e: CalEvent) => void;
  workWeek: boolean;
  workHour: boolean;
  labels: string[];
  locale: string;
}): ReactElement {
  const allDays: Date[] = Array.from({ length: 7 }, (_, i) => addDays(monday, i));
  const days = workWeek ? allDays.filter((d) => !WEEKEND_INDICES.has(weekdayIndex(d))) : allDays;
  const colCount = days.length;
  const today = isoDate(new Date());
  const HOURS = Array.from({ length: 24 }, (_, i) => i).filter(
    (h) => !workHour || (h >= WORK_HOUR_START && h < WORK_HOUR_END),
  );

  // Multi-day events (e.g. authored from the month view) don't have a single
  // hour slot to live in — render them as spanning bars above the hour grid,
  // same layout technique as the month view's week-row bars.
  const multiDayEvents = events.filter(isMultiDay);
  const { bars: allDayBars } = layoutWeekEvents(multiDayEvents, days);
  const allDayRowHeight = allDayBars.length > 0 ? MAX_LANES * (LANE_HEIGHT + LANE_GAP) : 0;

  return (
    <Card
      className="ak-cal-week-grid"
      style={{
        ...cardReset,
        borderRadius: TOKEN.radiusLg,
        overflow: 'hidden',
        overflowX: 'auto',
        background: TOKEN.surface,
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `48px repeat(${colCount}, 1fr)`,
          background: TOKEN.brand50,
          borderBottom: `1px solid ${TOKEN.border}`,
          minWidth: 480,
        }}
      >
        <div className="ak-cal-week-hourcol" />
        {days.map((d) => {
          const key = isoDate(d);
          const isT = key === today;
          return (
            <div
              key={key}
              style={{
                padding: '8px 4px',
                textAlign: 'center',
                borderLeft: `1px solid ${TOKEN.border}`,
              }}
            >
              <div
                style={{
                  fontSize: 'var(--font-size-xs, 11px)',
                  fontWeight: 700,
                  color: TOKEN.textSec,
                  textTransform: 'uppercase',
                }}
              >
                {labels[weekdayIndex(d)]}
              </div>
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 28,
                  height: 28,
                  borderRadius: '50%',
                  fontSize: 'var(--font-size-sm, 14px)',
                  fontWeight: isT ? 800 : 600,
                  color: isT ? 'var(--color-text-on-brand, #fff)' : TOKEN.text,
                  background: isT ? TOKEN.brand : 'transparent',
                  marginTop: 2,
                }}
              >
                {d.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      {allDayBars.length > 0 && (
        <div
          style={{
            position: 'relative',
            display: 'grid',
            gridTemplateColumns: `48px repeat(${colCount}, 1fr)`,
            minWidth: 480,
            borderBottom: `1px solid ${TOKEN.border}`,
            padding: '4px 0',
          }}
        >
          <div className="ak-cal-week-hourcol" />
          <div
            style={{
              gridColumn: `2 / span ${colCount}`,
              position: 'relative',
              display: 'grid',
              gridTemplateColumns: `repeat(${colCount}, 1fr)`,
              gridAutoRows: LANE_HEIGHT,
              rowGap: LANE_GAP,
              minHeight: allDayRowHeight,
              padding: '0 3px',
            }}
          >
            {allDayBars.map((b, bi) => (
              <div
                key={bi}
                style={{
                  gridColumn: `${b.startCol + 1} / span ${b.span}`,
                  gridRow: b.lane + 1,
                  padding: '0 1px',
                }}
              >
                <EventBarChip event={b.event} onClick={onEventClick} />
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={{ maxHeight: 640, overflowY: 'auto', minWidth: 480 }}>
        {HOURS.map((hour) => (
          <div
            key={hour}
            style={{
              display: 'grid',
              gridTemplateColumns: `48px repeat(${colCount}, 1fr)`,
              borderBottom: `1px solid ${TOKEN.border}`,
              minHeight: 48,
            }}
          >
            <div
              className="ak-cal-week-hourcol"
              style={{
                padding: '4px 6px 0',
                fontSize: 'var(--font-size-xs, 11px)',
                color: TOKEN.textTer,
                fontWeight: 600,
                textAlign: 'right',
                flexShrink: 0,
              }}
            >
              {fmt(new Date(2024, 0, 1, hour), locale, { hour: 'numeric' })}
            </div>

            {days.map((day, di) => {
              const slotEvents = eventsInHour(events, day, hour);
              const slotDate = new Date(day);
              slotDate.setHours(hour, 0, 0, 0);
              return (
                <div
                  key={di}
                  onClick={() => onSlotClick(slotDate)}
                  style={{
                    borderLeft: `1px solid ${TOKEN.border}`,
                    padding: '2px 3px',
                    cursor: 'pointer',
                    minHeight: 48,
                    position: 'relative',
                  }}
                >
                  {slotEvents.map((ev) => (
                    <EventChip key={String(ev.id)} event={ev} onClick={onEventClick} />
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </Card>
  );
}

/* ─── Modal helpers ────────────────────────────────────────────────────── */

const fieldLabel: CSSProperties = {
  display: 'block',
  fontSize: 'var(--font-size-sm, 13px)',
  fontWeight: 600,
  color: TOKEN.textSec,
  marginBottom: 4,
};

const textareaStyle: CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  borderRadius: TOKEN.radius,
  border: `1px solid ${TOKEN.borderStrong}`,
  fontSize: 'var(--font-size-sm, 14px)',
  color: TOKEN.text,
  background: TOKEN.surface,
  boxSizing: 'border-box',
  resize: 'vertical',
  minHeight: 72,
  fontFamily: 'inherit',
};

/* ─── Add / Edit modal ─────────────────────────────────────────────────── */

interface EventForm {
  title: string;
  description: string;
  startTime: string;
  endTime: string;
}

function EventModal({
  initial,
  editId,
  onClose,
  locale,
}: {
  initial: Partial<EventForm>;
  editId?: string | number;
  onClose: (saved: boolean) => void;
  locale: string;
}): ReactElement {
  const { t } = useTranslation('common');
  const [form, setForm] = useState<EventForm>({
    title: initial.title ?? '',
    description: initial.description ?? '',
    startTime: initial.startTime ?? '',
    endTime: initial.endTime ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const set = (k: keyof EventForm, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async () => {
    if (!form.title.trim() || !form.startTime || !form.endTime) {
      setError(t('message.eventFieldsRequired') || 'Title, start time and end time are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const isEdit = editId !== undefined;
      const url = isEdit
        ? apiUrl(`/api/calendar/events/${String(editId)}`)
        : apiUrl('/api/calendar/events');
      const method = isEdit ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        setError(t('message.eventSaveFailed') || 'Could not save event. Please try again.');
        return;
      }
      onClose(true);
    } catch {
      setError(t('message.networkErrorRetry') || 'Network error. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {editId !== undefined
              ? t('label.editEvent') || 'Edit event'
              : t('button.addEvent') || 'Add event'}
          </DialogTitle>
        </DialogHeader>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <label style={fieldLabel} htmlFor="ak-cal-title">
              {t('label.title') || 'Title'}
            </label>
            <Input
              id="ak-cal-title"
              value={form.title}
              onChange={(e) => set('title', e.target.value)}
              placeholder={t('label.eventTitle') || 'Event title'}
              autoFocus
              disabled={saving}
              aria-required="true"
            />
          </div>

          <div>
            <label style={fieldLabel} htmlFor="ak-cal-desc">
              {t('label.description') || 'Description'}
            </label>
            <textarea
              id="ak-cal-desc"
              style={textareaStyle}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              placeholder={t('label.optionalDescription') || 'Optional description'}
              rows={3}
              disabled={saving}
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={fieldLabel} htmlFor="ak-cal-start">
                {t('label.start') || 'Start'}
              </label>
              <DateTimeField
                id="ak-cal-start"
                locale={locale}
                value={form.startTime}
                onChange={(iso) => set('startTime', iso)}
                disabled={saving}
                aria-required="true"
                clearLabel={t('label.clear') || 'Clear'}
                todayLabel={t('label.today') || 'Today'}
                placeholder={t('label.selectDateTime') || 'Select date and time'}
              />
            </div>
            <div>
              <label style={fieldLabel} htmlFor="ak-cal-end">
                {t('label.end') || 'End'}
              </label>
              <DateTimeField
                id="ak-cal-end"
                locale={locale}
                value={form.endTime}
                onChange={(iso) => set('endTime', iso)}
                disabled={saving}
                aria-required="true"
                clearLabel={t('label.clear') || 'Clear'}
                todayLabel={t('label.today') || 'Today'}
                placeholder={t('label.selectDateTime') || 'Select date and time'}
              />
            </div>
          </div>

          {error && (
            <div
              role="alert"
              style={{
                fontSize: 'var(--font-size-sm, 13px)',
                color: 'var(--color-danger-600,#dc2626)',
                fontWeight: 600,
              }}
            >
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onClose(false)}>
            {t('button.cancel') || 'Cancel'}
          </Button>
          <Button
            variant="default"
            style={{ opacity: saving ? 0.7 : 1 }}
            onClick={() => {
              void handleSubmit();
            }}
            disabled={saving}
            aria-disabled={saving}
          >
            {saving
              ? t('button.saving') || 'Saving…'
              : editId !== undefined
                ? t('button.update') || 'Update'
                : t('button.addEvent') || 'Add event'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Detail / delete modal ────────────────────────────────────────────── */

function DetailModal({
  event,
  onClose,
  onEdit,
  onDeleted,
  locale,
}: {
  event: CalEvent;
  onClose: () => void;
  onEdit: () => void;
  onDeleted: () => void;
  locale: string;
}): ReactElement {
  const { t } = useTranslation('common');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const isOwned = event.entityType === 'USER_MANAGED' || !event.entityType;

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const handleDelete = async () => {
    setDeleting(true);
    setError('');
    try {
      const res = await fetch(apiUrl(`/api/calendar/events/${String(event.id)}`), {
        method: 'DELETE',
      });
      if (!res.ok) {
        setError(t('message.eventDeleteFailed') || 'Delete failed. Please try again.');
        return;
      }
      onDeleted();
    } catch {
      setError(t('message.networkError') || 'Network error.');
    } finally {
      setDeleting(false);
    }
  };

  const color = eventColor(event);
  const rowStyle: CSSProperties = {
    display: 'flex',
    gap: 12,
    fontSize: 'var(--font-size-sm, 14px)',
    color: TOKEN.text,
    lineHeight: 1.5,
  };
  const rowLabel: CSSProperties = {
    fontWeight: 700,
    color: TOKEN.textSec,
    minWidth: 80,
    flexShrink: 0,
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span
              style={{
                display: 'inline-block',
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: color,
                flexShrink: 0,
              }}
            />
            <DialogTitle>{event.title}</DialogTitle>
          </div>
        </DialogHeader>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {event.description && (
            <div style={rowStyle}>
              <span style={rowLabel}>{t('label.note') || 'Note'}</span>
              <span>{event.description}</span>
            </div>
          )}
          <div style={rowStyle}>
            <span style={rowLabel}>{t('label.start') || 'Start'}</span>
            <span>{fmtDatetime(event.startTime, locale)}</span>
          </div>
          {event.endTime && (
            <div style={rowStyle}>
              <span style={rowLabel}>{t('label.end') || 'End'}</span>
              <span>{fmtDatetime(event.endTime, locale)}</span>
            </div>
          )}
          {event.entityType && (
            <div style={rowStyle}>
              <span style={rowLabel}>{t('label.type') || 'Type'}</span>
              <span
                style={{
                  color,
                  fontWeight: 600,
                  fontSize: 'var(--font-size-xs, 12px)',
                  textTransform: 'uppercase',
                }}
              >
                {entityTypeLabel(t, event.entityType)}
              </span>
            </div>
          )}
        </div>

        {error && (
          <div
            style={{
              fontSize: 'var(--font-size-sm, 13px)',
              color: 'var(--color-danger-600,#dc2626)',
              fontWeight: 600,
              marginTop: 10,
            }}
          >
            {error}
          </div>
        )}

        <DialogFooter style={{ flexWrap: 'wrap' }}>
          {isOwned && (
            <Button
              variant="destructive"
              style={{ opacity: deleting ? 0.7 : 1 }}
              onClick={() => {
                void handleDelete();
              }}
              disabled={deleting}
              aria-disabled={deleting}
            >
              {deleting ? t('button.deleting') || 'Deleting…' : t('button.delete') || 'Delete'}
            </Button>
          )}
          {isOwned && (
            <Button variant="ghost" onClick={onEdit}>
              {t('button.edit') || 'Edit'}
            </Button>
          )}
          <Button variant="default" onClick={onClose}>
            {t('label.close') || 'Close'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ─── Toolbar ──────────────────────────────────────────────────────────── */

function ViewTabs({
  view,
  onViewChange,
}: {
  view: CalView;
  onViewChange: (v: CalView) => void;
}): ReactElement {
  const { t } = useTranslation('common');
  const tabStyle = (active: boolean): CSSProperties => ({
    padding: '6px 14px',
    borderRadius: TOKEN.radius,
    fontSize: 'var(--font-size-sm, 13px)',
    fontWeight: 600,
    border: 'none',
    cursor: 'pointer',
    background: active ? TOKEN.brand : 'transparent',
    color: active ? 'var(--color-text-on-brand, #fff)' : TOKEN.textSec,
  });
  return (
    <div
      style={{
        display: 'flex',
        background: TOKEN.brand50,
        borderRadius: TOKEN.radius,
        padding: 3,
        gap: 2,
      }}
    >
      <button
        className="ak-focus"
        style={tabStyle(view === 'month')}
        onClick={() => onViewChange('month')}
        aria-pressed={view === 'month'}
      >
        {t('label.month') || 'Month'}
      </button>
      <button
        className="ak-focus"
        style={tabStyle(view === 'week')}
        onClick={() => onViewChange('week')}
        aria-pressed={view === 'week'}
      >
        {t('label.week') || 'Week'}
      </button>
    </div>
  );
}

/* ─── Main component ───────────────────────────────────────────────────── */

export function CalendarBox({
  title,
  subtitle,
  titleFontSize,
  defaultView = 'month',
  addEventLabel,
  emptyLabel = 'No events this period.',
  signedOutTitle,
  signedOutBody = 'Sign in to see your scheduled sessions and add personal events.',
  signInLabel = 'Sign in',
  signInHref = '/sign-in',
  defaultWorkWeek = false,
  defaultWorkHour = false,
  workWeekLabel,
  workHourLabel,
  todayLabel,
  prevLabel,
  nextLabel,
  dayFormat = 'short',
  paddingOuter,
  paddingInner,
}: CalendarBoxProps): ReactElement {
  const { t } = useTranslation('common');
  const user = useCurrentUser<{ name?: string }>();
  const { locale } = useLocale();
  const months = useMemo(() => monthNames(locale), [locale]);
  const days = useMemo(
    () => dayNames(locale, dayFormat).map((d) => d.toUpperCase()),
    [locale, dayFormat],
  );

  // Toolbar chrome (today/prev/next nav + Work week/Work hours toggles +
  // Add-event button) + the section title always follow the active site
  // locale unless an operator explicitly overrides them in the panel —
  // these were previously hardcoded English literals as prop defaults
  // (both here and in the block's seed-data `defaults`), so switching the
  // site language never translated them.
  const resolvedTitle = title ?? t('label.myCalendar');
  const resolvedAddEventLabel = addEventLabel ?? t('button.addEvent');
  const resolvedWorkWeekLabel = workWeekLabel ?? t('label.workWeek');
  const resolvedWorkHourLabel = workHourLabel ?? t('label.workHour');
  const resolvedTodayLabel = todayLabel ?? t('label.today');
  const resolvedPrevLabel = prevLabel ?? t('label.previous');
  const resolvedNextLabel = nextLabel ?? t('label.next');
  const resolvedSignedOutTitle = signedOutTitle ?? t('label.myCalendar');

  const today = new Date();
  const [view, setView] = useState<CalView>(defaultView);
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth());
  const [monday, setMonday] = useState<Date>(getMonday(today));
  const [workWeek, setWorkWeek] = useState(defaultWorkWeek);
  const [workHour, setWorkHour] = useState(defaultWorkHour);

  const [events, setEvents] = useState<CalEvent[]>([]);
  const [authBlocked, setAuthBlocked] = useState(false);
  const fetchCtrl = useRef<AbortController | null>(null);

  const [detail, setDetail] = useState<CalEvent | null>(null);
  const [editState, setEditState] = useState<{
    open: boolean;
    initial: Partial<EventForm>;
    editId?: string | number;
  }>({ open: false, initial: {} });

  const fetchEvents = useCallback(
    (startTime: string, endTime: string) => {
      if (!user) return;
      fetchCtrl.current?.abort();
      const ctrl = new AbortController();
      fetchCtrl.current = ctrl;

      fetch(apiUrl('/api/calendar/events/search'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filter: { startTime, endTime }, maxResults: 500 }),
        signal: ctrl.signal,
      })
        .then(async (res) => {
          if (res.status === 401) {
            if (!ctrl.signal.aborted) setAuthBlocked(true);
            return;
          }
          if (!res.ok || ctrl.signal.aborted) return;
          const data = (await res.json()) as { resultSet?: CalEvent[] };
          if (!ctrl.signal.aborted) {
            setEvents(Array.isArray(data.resultSet) ? data.resultSet : []);
          }
        })
        .catch(() => {
          if (!ctrl.signal.aborted) setEvents([]);
        });
    },
    [user],
  );

  // Fetch window for the active view. Padded 7 days on each side in BOTH
  // views: the backend filters by startTime falling inside [start, end], so
  // a multi-day event that started before the visible window (e.g. authored
  // from the month view, spanning into this week) would be silently dropped
  // by an exact-window fetch — the service just returns an empty result set
  // instead of erroring, which looked like "events don't show in week view."
  const fetchWindow = useCallback((): { start: string; end: string } => {
    if (view === 'month') {
      return {
        start: addDays(new Date(year, month, 1), -7).toISOString(),
        end: addDays(new Date(year, month + 1, 0, 23, 59, 59), 7).toISOString(),
      };
    }
    return {
      start: addDays(monday, -7).toISOString(),
      end: addDays(monday, 14).toISOString(),
    };
  }, [view, year, month, monday]);

  useEffect(() => {
    if (!user) return;
    const { start, end } = fetchWindow();
    fetchEvents(start, end);
    return () => {
      fetchCtrl.current?.abort();
    };
  }, [user, fetchWindow, fetchEvents]);

  const goPrev = () => {
    if (view === 'month') {
      if (month === 0) {
        setMonth(11);
        setYear((y) => y - 1);
      } else setMonth((m) => m - 1);
    } else {
      setMonday((d) => addDays(d, -7));
    }
  };
  const goNext = () => {
    if (view === 'month') {
      if (month === 11) {
        setMonth(0);
        setYear((y) => y + 1);
      } else setMonth((m) => m + 1);
    } else {
      setMonday((d) => addDays(d, 7));
    }
  };
  const goToday = () => {
    const t = new Date();
    setYear(t.getFullYear());
    setMonth(t.getMonth());
    setMonday(getMonday(t));
  };

  const openAdd = (prefill?: Partial<EventForm>) => {
    setEditState({ open: true, initial: prefill ?? {}, editId: undefined });
    setDetail(null);
  };
  const openEdit = (ev: CalEvent) => {
    setEditState({
      open: true,
      initial: {
        title: ev.title,
        description: ev.description ?? '',
        startTime: ev.startTime ?? '',
        endTime: ev.endTime ?? '',
      },
      editId: ev.id,
    });
    setDetail(null);
  };
  const closeModal = (saved: boolean) => {
    setEditState({ open: false, initial: {} });
    if (saved) {
      const t = fetchWindow();
      fetchEvents(t.start, t.end);
    }
  };
  const handleDeleted = () => {
    setDetail(null);
    if (detail) {
      setEvents((prev) => prev.filter((e) => String(e.id) !== String(detail.id)));
    }
  };
  const handleDayClick = (d: Date) => {
    const h = d.getHours();
    const start = new Date(d);
    start.setHours(h, 0, 0, 0);
    const end = new Date(d);
    end.setHours(h + 1, 0, 0, 0);
    openAdd({ startTime: start.toISOString(), endTime: end.toISOString() });
  };

  if (!user || authBlocked) {
    return (
      <SectionShell paddingOuter={paddingOuter} paddingInner={paddingInner}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 14,
            background: `linear-gradient(135deg, ${TOKEN.brand50}, ${TOKEN.surface})`,
            border: `1px solid ${TOKEN.border}`,
            borderRadius: TOKEN.radiusLg,
            padding: 'clamp(20px, 4vw, 32px)',
          }}
        >
          <span
            style={{
              fontSize: 'var(--font-size-xs, 12px)',
              fontWeight: 700,
              letterSpacing: 'var(--font-tracking-widest, 0.08em)',
              textTransform: 'uppercase',
              color: TOKEN.textTer,
            }}
          >
            CALENDAR
          </span>
          <h3
            style={{
              margin: 0,
              fontSize: 'clamp(20px, 2.5vw, 26px)',
              fontWeight: 800,
              color: TOKEN.text,
            }}
          >
            {resolvedSignedOutTitle}
          </h3>
          <p style={{ margin: 0, color: TOKEN.textSec }}>{signedOutBody}</p>
          <a
            href={signInHref}
            style={{
              padding: '10px 20px',
              borderRadius: TOKEN.radius,
              background: TOKEN.text,
              color: TOKEN.surface,
              fontSize: 'var(--font-size-sm, 14px)',
              fontWeight: 600,
              textDecoration: 'none',
            }}
          >
            {signInLabel}
          </a>
        </div>
      </SectionShell>
    );
  }

  const hasEvents = events.length > 0;

  return (
    <SectionShell
      title={resolvedTitle}
      subtitle={subtitle}
      titleFontSize={titleFontSize}
      paddingOuter={paddingOuter}
      paddingInner={paddingInner}
    >
      <CalendarBoxStyles />

      <div className="ak-cal-root">
        <CalendarToolbar
          title={
            view === 'month'
              ? `${months[month]} ${year}`
              : `${fmt(monday, locale, { month: 'short', day: 'numeric' })} – ${fmt(addDays(monday, 6), locale, { month: 'short', day: 'numeric', year: 'numeric' })}`
          }
          onPrev={goPrev}
          onNext={goNext}
          onToday={goToday}
          todayLabel={resolvedTodayLabel}
          prevLabel={resolvedPrevLabel}
          nextLabel={resolvedNextLabel}
          workWeek={workWeek}
          workHour={workHour}
          onToggleWorkWeek={() => setWorkWeek((w) => !w)}
          onToggleWorkHour={() => setWorkHour((h) => !h)}
          workWeekLabel={resolvedWorkWeekLabel}
          workHourLabel={resolvedWorkHourLabel}
          trailing={
            <>
              <ViewTabs view={view} onViewChange={setView} />
              <Button variant="default" onClick={() => openAdd()}>
                {resolvedAddEventLabel}
              </Button>
            </>
          }
        />

        {!hasEvents && (
          <div
            role="status"
            aria-live="polite"
            style={{
              padding: '24px',
              textAlign: 'center',
              color: TOKEN.textSec,
              fontSize: 'var(--font-size-sm, 14px)',
              border: `1px dashed ${TOKEN.borderStrong}`,
              borderRadius: TOKEN.radiusLg,
              marginBottom: 16,
            }}
          >
            {emptyLabel}
          </div>
        )}

        {view === 'month' ? (
          <MonthGrid
            year={year}
            month={month}
            events={events}
            onDayClick={handleDayClick}
            onEventClick={(ev) => setDetail(ev)}
            workWeek={workWeek}
            labels={days}
          />
        ) : (
          <WeekGrid
            monday={monday}
            events={events}
            onSlotClick={handleDayClick}
            onEventClick={(ev) => setDetail(ev)}
            workWeek={workWeek}
            workHour={workHour}
            labels={days}
            locale={locale}
          />
        )}

        <div
          style={{
            display: 'flex',
            gap: 16,
            flexWrap: 'wrap',
            marginTop: 12,
            fontSize: 'var(--font-size-xs, 12px)',
            color: TOKEN.textSec,
          }}
        >
          {Object.entries(entityColor).map(([k, c]) => (
            <span key={k} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
              <span
                style={{
                  display: 'inline-block',
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  background: c,
                }}
              />
              {entityTypeLabel(t, k)}
            </span>
          ))}
        </div>
      </div>

      {detail && (
        <DetailModal
          event={detail}
          onClose={() => setDetail(null)}
          onEdit={() => openEdit(detail)}
          onDeleted={handleDeleted}
          locale={locale}
        />
      )}

      {editState.open && (
        <EventModal
          initial={editState.initial}
          editId={editState.editId}
          onClose={closeModal}
          locale={locale}
        />
      )}
    </SectionShell>
  );
}
